import { Link, useRouterState } from "@tanstack/react-router";
import { Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { US_MARKET_ENABLED } from "@/lib/markets";

/**
 * Country / region switcher. Sends shoppers to the localized landing page
 * (fi → `/`, us → `/us`) rather than mutating the current URL, so the
 * destination matches the hreflang alternates advertised in <head> and the
 * sitemap. Rendered in both the header and the footer.
 */

type CountryId = "fi" | "us";

const ALL_OPTIONS: Array<{
  id: CountryId;
  href: "/" | "/us";
  flag: string;
  label: string;
  sublabel: string;
  hrefLang: string;
}> = [
  { id: "fi", href: "/", flag: "🇫🇮", label: "Suomi", sublabel: "EUR · Suomi", hrefLang: "fi" },
  { id: "us", href: "/us", flag: "🇺🇸", label: "United States", sublabel: "USD · English", hrefLang: "en-US" },
];

/** The US market is currently not operated — see src/lib/markets.ts. */
const OPTIONS = ALL_OPTIONS.filter((o) => o.id !== "us" || US_MARKET_ENABLED);

function useCurrentCountry(): CountryId {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  if (pathname === "/us" || pathname.startsWith("/us/")) return "us";
  if (search?.market === "us") return "us";
  return "fi";
}

export function CountrySwitcher({
  variant = "header",
}: {
  variant?: "header" | "footer";
}) {
  const current = useCurrentCountry();
  const active = OPTIONS.find((o) => o.id === current) ?? OPTIONS[0];
  const isFi = current === "fi";
  const t = {
    triggerAria: isFi ? "Toimitusmaa" : "Shipping country",
    heading: isFi ? "Toimitus maahan" : "Shipping to",
  };

  const triggerClass =
    variant === "footer"
      ? "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      : "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={triggerClass}
        aria-label={`${t.triggerAria}: ${active.id === "us" ? "US · USD" : "FI · EUR"}`}
      >
        <span aria-hidden className="text-base leading-none">{active.flag}</span>
        <span className="uppercase tracking-wide text-[11px]">{active.id === "us" ? "US · USD" : "FI · EUR"}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={variant === "footer" ? "start" : "end"} className="min-w-[14rem]">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          {t.heading}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((o) => (
          <DropdownMenuItem key={o.id} asChild className="cursor-pointer">
            {/* Real <a> semantics via Link — hrefLang matches the target's
                declared alternate so crawlers can follow language signals. */}
            <Link
              to={o.href}
              hrefLang={o.hrefLang}
              className="flex items-center gap-2"
            >
              <span aria-hidden className="text-base leading-none">{o.flag}</span>
              <span className="flex-1">
                {o.label}
                <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {o.sublabel}
                </span>
              </span>
              {o.id === current && <Check className="h-3.5 w-3.5 text-foreground" />}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}